import {
  useBrowseProductCardsApi,
  useBrowseSubscriptionPlanApi,
} from '@aibees/shared/api';
import {
  PlanInterval,
  PlanSlug,
  type PlanCardConfig,
} from '@aibees/shared/definition';
import { useMemo } from 'react';
import { buildPlanCardConfig, productFamilyKey } from '../catalog';

export const USAGE_BASED_SLUG = PlanSlug.BUILDER;

export interface PlanConfigs {
  /** Configs for every product the API returns, keyed by product id. */
  byId: Record<string, PlanCardConfig>;
  /**
   * Configs keyed by `family ?? slug`. Use this to resolve a config for a
   * product that came from a *different* endpoint (or `return=` shape) than
   * `useBrowseProductCardsApi` — product ids aren't guaranteed to match across
   * those, whereas family/slug is the backend's own grouping key.
   */
  byFamily: Record<string, PlanCardConfig>;
  /** Configs keyed by product display name (kept for legacy callers). */
  byName: Record<string, PlanCardConfig>;
  isLoading: boolean;
}

export function usePlanConfigs(): PlanConfigs {
  const { data: cards, isLoading: cardsLoading } = useBrowseProductCardsApi();
  const { data: monthlyPlans, isLoading: plansLoading } =
    useBrowseSubscriptionPlanApi(PlanInterval.MONTH);

  const isLoading = cardsLoading || plansLoading;

  return useMemo(() => {
    if (!cards || !monthlyPlans) {
      return {
        byId: {},
        byFamily: {},
        byName: {},
        isLoading,
      };
    }

    const byId: Record<string, PlanCardConfig> = {};
    const byFamily: Record<string, PlanCardConfig> = {};
    const byName: Record<string, PlanCardConfig> = {};

    // Build a config for EVERY product the API returns so the page works for
    // plans whose slug isn't one of the four well-known tiers.
    for (const product of cards.products) {
      const config = buildPlanCardConfig(product.id, cards, monthlyPlans);
      if (!config) continue;
      byId[product.id] = config;
      const family = productFamilyKey(product);
      if (family) byFamily[family] = config;
      if (product.name) byName[product.name] = config;
    }

    return { byId, byFamily, byName, isLoading };
  }, [cards, monthlyPlans, isLoading]);
}
